import { ReactNode } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Copy, Edit, Eye, Trash2 } from "lucide-react"

interface TableColumn<T> {
  key: string
  label: string
  render?: (value: any, item: T) => ReactNode
  sortable?: boolean
  width?: string
}

interface ResourceTableProps<T> {
  data: T[]
  columns: TableColumn<T>[]
  loading?: boolean
  onView?: (item: T) => void
  onEdit?: (item: T) => void
  onDelete?: (item: T) => void
  onClone?: (item: T) => void
  onRowClick?: (item: T) => void
  emptyMessage?: string
  className?: string
}

/**
 * Tabela genérica para exibição de recursos administrativos
 */
export function ResourceTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  onView,
  onEdit,
  onDelete,
  onClone,
  onRowClick,
  emptyMessage = "Nenhum registro encontrado",
  className,
}: ResourceTableProps<T>) {
  // Função para formatar valores automaticamente
  const formatValue = (value: any): ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground">-</span>
    }

    if (typeof value === "boolean") {
      return <Badge variant={value ? "default" : "secondary"}>{value ? "Sim" : "Não"}</Badge>
    }

    if (typeof value === "string" && value.includes("T") && value.includes("Z")) {
      // Provavelmente uma data ISO
      try {
        return new Date(value).toLocaleString("pt-BR")
      } catch {
        return value
      }
    }

    if (Array.isArray(value)) {
      return value.join(", ")
    }

    return String(value)
  }

  const hasActions = onView || onEdit || onDelete || onClone

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="text-muted-foreground rounded-lg border p-8 text-center">{emptyMessage}</div>
    )
  }

  return (
    <div className={`rounded-lg border ${className || ""}`}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(column => (
              <TableHead
                key={column.key}
                style={{ width: column.width }}
                className={column.sortable ? "hover:bg-muted/50 cursor-pointer" : ""}
              >
                {column.label}
              </TableHead>
            ))}
            {hasActions && <TableHead className="w-32">Ações</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, index) => (
            <TableRow
              key={item.id || item._id || index}
              className={onRowClick ? "hover:bg-muted/50 cursor-pointer" : ""}
              onClick={() => onRowClick?.(item)}
            >
              {columns.map(column => (
                <TableCell key={column.key}>
                  {column.render
                    ? column.render(item[column.key], item)
                    : formatValue(item[column.key])}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell>
                  <div className="flex items-center gap-1">
                    {onView && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation()
                          onView(item)
                        }}
                        className="h-8 w-8 p-0"
                        title="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    {onEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation()
                          onEdit(item)
                        }}
                        className="h-8 w-8 p-0"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {onClone && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation()
                          onClone(item)
                        }}
                        className="h-8 w-8 p-0"
                        title="Clonar"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                    {onDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation()
                          onDelete(item)
                        }}
                        className="text-destructive hover:text-destructive h-8 w-8 p-0"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
